import { useState, useContext, useEffect } from 'react';
import { AuthContext } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import { Shield, AlertTriangle, Ban, Smartphone, Download, BellRing } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { QRCodeCanvas } from 'qrcode.react';
import { subscribePush, unsubscribePush, isPushSubscribed, getPermissionStatus, getPushSupport } from '@/services/pushNotifications';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const { token } = useContext(AuthContext);
  const [reportUserId, setReportUserId] = useState('');
  const [reportReason, setReportReason] = useState('');
  const [blockUserId, setBlockUserId] = useState('');
  const [moderateContent, setModerateContent] = useState('');
  const [moderationResult, setModerationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [pushSupport, setPushSupport] = useState({ supported: true, reason: null });
  const appUrl = window.location.origin;

  useEffect(() => {
    isPushSubscribed().then(setPushEnabled);
    setPushPermission(getPermissionStatus());
    setPushSupport(getPushSupport());
  }, []);

  const togglePush = async () => {
    setPushLoading(true);
    try {
      if (pushEnabled) {
        await unsubscribePush(token);
        setPushEnabled(false);
        toast.success('Push notifications disabled');
      } else {
        const result = await subscribePush(token);
        if (result.ok) {
          setPushEnabled(true);
          setPushPermission('granted');
          toast.success('Push notifications enabled!');
        } else if (result.error === 'blocked') {
          setPushPermission('denied');
          toast.error('Notifications are blocked. Please enable them in your browser settings.');
        } else if (result.error === 'dismissed') {
          toast.error('Please allow notifications when prompted.');
        } else {
          toast.error(result.error || 'Could not enable push notifications.');
        }
      }
    } catch {
      toast.error('Failed to update push settings');
    } finally {
      setPushLoading(false);
    }
  };

  const handleReportUser = async () => {
    if (!reportUserId || !reportReason) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await axios.post(
        `${API}/safety/report`,
        { reported_user_id: reportUserId, reason: reportReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Report submitted successfully');
      setReportUserId('');
      setReportReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit report');
    }
  };

  const handleBlockUser = async () => {
    if (!blockUserId) {
      toast.error('Please enter a user ID');
      return;
    }

    try {
      await axios.post(
        `${API}/safety/block`,
        { blocked_user_id: blockUserId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User blocked successfully');
      setBlockUserId('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to block user');
    }
  };

  const handleModerateContent = async () => {
    if (!moderateContent) {
      toast.error('Please enter content to moderate');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/safety/moderate`,
        { content: moderateContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setModerationResult(response.data);
    } catch (error) {
      toast.error('Failed to moderate content');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-peach-50 to-teal-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 py-8 pt-24">
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <Shield className="w-10 h-10 text-teal" />
            <h1 className="text-4xl font-bold">Safety & Settings</h1>
          </div>
          <p className="text-gray-600 text-lg">Manage your safety preferences and help keep the community safe</p>
        </div>

        <Tabs defaultValue="report" className="space-y-6">
          <TabsList className="glass-dark p-1 rounded-2xl">
            <TabsTrigger
              value="report"
              data-testid="report-tab"
              className="rounded-xl data-[state=active]:bg-coral data-[state=active]:text-white"
            >
              Report User
            </TabsTrigger>
            <TabsTrigger
              value="block"
              data-testid="block-tab"
              className="rounded-xl data-[state=active]:bg-coral data-[state=active]:text-white"
            >
              Block User
            </TabsTrigger>
            <TabsTrigger
              value="moderate"
              data-testid="moderate-tab"
              className="rounded-xl data-[state=active]:bg-coral data-[state=active]:text-white"
            >
              Content Moderation
            </TabsTrigger>
            <TabsTrigger
              value="download"
              data-testid="download-tab"
              className="rounded-xl data-[state=active]:bg-coral data-[state=active]:text-white"
            >
              Download App
            </TabsTrigger>
            <TabsTrigger
              value="push"
              data-testid="push-tab"
              className="rounded-xl data-[state=active]:bg-coral data-[state=active]:text-white"
            >
              Push Notifications
            </TabsTrigger>
          </TabsList>

          {/* Report User Tab */}
          <TabsContent value="report">
            <div className="glass-dark rounded-3xl p-8 shadow-lg" data-testid="report-section">
              <div className="flex items-center space-x-3 mb-6">
                <AlertTriangle className="w-8 h-8 text-coral" />
                <h2 className="text-2xl font-bold">Report a User</h2>
              </div>
              <p className="text-gray-600 mb-6">
                If you've encountered inappropriate behavior, please report it. Our team will review the report.
              </p>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="report-user-id" className="text-gray-700 font-medium">User ID</Label>
                  <Input
                    id="report-user-id"
                    data-testid="report-user-id-input"
                    placeholder="Enter the user ID to report"
                    value={reportUserId}
                    onChange={(e) => setReportUserId(e.target.value)}
                    className="mt-1 rounded-xl"
                  />
                  <p className="text-sm text-gray-500 mt-1">You can find the user ID in their profile URL</p>
                </div>

                <div>
                  <Label htmlFor="report-reason" className="text-gray-700 font-medium">Reason for Report</Label>
                  <Textarea
                    id="report-reason"
                    data-testid="report-reason-input"
                    placeholder="Please describe what happened..."
                    value={reportReason}
                    onChange={(e) => setReportReason(e.target.value)}
                    className="mt-1 rounded-xl min-h-[120px]"
                  />
                </div>

                <Button
                  data-testid="submit-report-btn"
                  onClick={handleReportUser}
                  className="w-full py-6 rounded-xl bg-white text-black border-2 border-teal hover:bg-teal hover:text-white font-semibold"
                >
                  Submit Report
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Block User Tab */}
          <TabsContent value="block">
            <div className="glass-dark rounded-3xl p-8 shadow-lg" data-testid="block-section">
              <div className="flex items-center space-x-3 mb-6">
                <Ban className="w-8 h-8 text-red-500" />
                <h2 className="text-2xl font-bold">Block a User</h2>
              </div>
              <p className="text-gray-600 mb-6">
                Blocking a user will prevent them from messaging you or seeing your profile.
              </p>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="block-user-id" className="text-gray-700 font-medium">User ID</Label>
                  <Input
                    id="block-user-id"
                    data-testid="block-user-id-input"
                    placeholder="Enter the user ID to block"
                    value={blockUserId}
                    onChange={(e) => setBlockUserId(e.target.value)}
                    className="mt-1 rounded-xl"
                  />
                  <p className="text-sm text-gray-500 mt-1">You can find the user ID in their profile URL</p>
                </div>

                <Button
                  data-testid="block-user-btn"
                  onClick={handleBlockUser}
                  className="w-full py-6 rounded-xl bg-red-500 hover:bg-red-600 text-white"
                >
                  Block User
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Content Moderation Tab */}
          <TabsContent value="moderate">
            <div className="glass-dark rounded-3xl p-8 shadow-lg" data-testid="moderate-section">
              <div className="flex items-center space-x-3 mb-6">
                <Shield className="w-8 h-8 text-teal" />
                <h2 className="text-2xl font-bold">AI Content Moderation</h2>
              </div>
              <p className="text-gray-600 mb-6">
                Test our AI moderation system. Enter any content to check if it's appropriate.
              </p>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="moderate-content" className="text-gray-700 font-medium">Content to Moderate</Label>
                  <Textarea
                    id="moderate-content"
                    data-testid="moderate-content-input"
                    placeholder="Enter content to check for safety..."
                    value={moderateContent}
                    onChange={(e) => setModerateContent(e.target.value)}
                    className="mt-1 rounded-xl min-h-[120px]"
                  />
                </div>

                <Button
                  data-testid="moderate-content-btn"
                  onClick={handleModerateContent}
                  disabled={loading}
                  className="w-full py-6 rounded-xl bg-white text-black border-2 border-teal hover:bg-teal hover:text-white font-semibold"
                >
                  {loading ? 'Analyzing...' : 'Check Content'}
                </Button>

                {moderationResult && (
                  <div
                    data-testid="moderation-result"
                    className={`p-6 rounded-2xl border-2 ${
                      moderationResult.is_safe
                        ? 'bg-green-50 border-green-500'
                        : 'bg-red-50 border-red-500'
                    }`}
                  >
                    <div className="flex items-center space-x-2 mb-2">
                      {moderationResult.is_safe ? (
                        <Shield className="w-6 h-6 text-green-600" />
                      ) : (
                        <AlertTriangle className="w-6 h-6 text-red-600" />
                      )}
                      <span className={`font-bold text-lg ${
                        moderationResult.is_safe ? 'text-green-700' : 'text-red-700'
                      }`}>
                        {moderationResult.is_safe ? 'Content is Safe' : 'Content May Be Inappropriate'}
                      </span>
                    </div>
                    <p className="text-gray-700">{moderationResult.analysis}</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Download App Tab */}
          <TabsContent value="download">
            <div className="glass-dark rounded-3xl p-8 shadow-lg">
              <div className="flex items-center space-x-3 mb-6">
                <Smartphone className="w-8 h-8 text-teal" />
                <h2 className="text-2xl font-bold">Install CultraMeet on Your Phone</h2>
              </div>
              <p className="text-gray-600 mb-8">
                Share this QR code with friends or scan it on another device to install the CultraMeet app.
              </p>

              <div className="grid md:grid-cols-2 gap-8">
                {/* QR Code */}
                <div className="flex flex-col items-center">
                  <div className="bg-white p-6 rounded-2xl shadow-lg mb-4">
                    <QRCodeCanvas
                      value={appUrl}
                      size={200}
                      level="H"
                      includeMargin={true}
                      fgColor="#14B8A6"
                      bgColor="#FFFFFF"
                    />
                  </div>
                  <p className="text-sm text-gray-600 text-center">
                    Scan with phone camera to install
                  </p>
                </div>

                {/* Instructions */}
                <div className="space-y-4">
                  <h3 className="text-xl font-bold mb-4">Installation Steps:</h3>
                  
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-teal text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
                    <div>
                      <p className="font-semibold">Scan QR Code</p>
                      <p className="text-sm text-gray-600">Use your phone's camera app to scan the code</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-teal text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
                    <div>
                      <p className="font-semibold">Open Link</p>
                      <p className="text-sm text-gray-600">Tap the notification to open in your browser</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-teal text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
                    <div>
                      <p className="font-semibold">Install</p>
                      <p className="text-sm text-gray-600">
                        <strong>iOS:</strong> Tap Share → "Add to Home Screen"<br />
                        <strong>Android:</strong> Tap menu → "Install App"
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-teal/10 rounded-xl">
                    <div className="flex items-center space-x-2 text-teal">
                      <Download className="w-5 h-5" />
                      <p className="font-semibold">Works on all smartphones</p>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      The app will work like a native app with offline support, notifications, and home screen icon.
                    </p>
                  </div>
                </div>
              </div>

              {/* Share Section */}
              <div className="mt-8 p-6 bg-gradient-to-r from-teal/10 to-coral/10 rounded-2xl">
                <h4 className="font-bold mb-2">Share CultraMeet</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Take a screenshot of the QR code to share with friends via WhatsApp, Instagram, or any messaging app!
                </p>
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(appUrl);
                    toast.success('App link copied to clipboard!');
                  }}
                  className="bg-teal text-white rounded-xl"
                >
                  Copy App Link
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Push Notifications Tab */}
          <TabsContent value="push">
            <div className="glass-dark rounded-3xl p-8 shadow-lg" data-testid="push-section">
              <div className="flex items-center space-x-3 mb-6">
                <BellRing className="w-8 h-8 text-coral" />
                <h2 className="text-2xl font-bold">Push Notifications</h2>
              </div>
              <p className="text-gray-600 mb-6">
                Get notified about new messages, connection requests, and missed calls even when your browser is closed.
              </p>

              {!pushSupport.supported ? (
                <div className="bg-red-50 border border-red-200 rounded-2xl p-6" data-testid="push-unsupported">
                  <p className="font-semibold text-red-700">Not Supported</p>
                  <p className="text-sm text-red-600 mt-1">{pushSupport.reason}</p>
                  <p className="text-sm text-gray-500 mt-2">Try using Chrome, Firefox, or Edge for push notification support.</p>
                </div>
              ) : pushPermission === 'denied' ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6" data-testid="push-blocked">
                  <p className="font-semibold text-yellow-800">Notifications Blocked</p>
                  <p className="text-sm text-yellow-700 mt-2">
                    Your browser has blocked notifications for this site. To enable them:
                  </p>
                  <ol className="list-decimal list-inside text-sm text-gray-700 mt-3 space-y-2">
                    <li>Click the <strong>lock icon</strong> (or site info icon) in your browser's address bar</li>
                    <li>Find <strong>"Notifications"</strong> in the permissions list</li>
                    <li>Change it from <strong>"Block"</strong> to <strong>"Allow"</strong></li>
                    <li>Refresh this page and try enabling again</li>
                  </ol>
                  <Button
                    onClick={() => window.location.reload()}
                    className="mt-4 bg-yellow-600 hover:bg-yellow-700 text-white rounded-full px-6"
                  >
                    Refresh Page
                  </Button>
                </div>
              ) : (
                <div className="bg-white rounded-2xl p-6 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold">Background Push Notifications</p>
                      <p className="text-sm text-gray-500 mt-1">
                        {pushEnabled
                          ? 'You will receive notifications on this device'
                          : pushPermission === 'granted'
                            ? 'Permissions granted. Click Enable to activate.'
                            : 'Your browser will ask for permission when you click Enable.'
                        }
                      </p>
                    </div>
                    <Button
                      data-testid="push-toggle-btn"
                      onClick={togglePush}
                      disabled={pushLoading}
                      className={`rounded-full px-6 ${pushEnabled ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-coral hover:bg-coral-dark text-white'}`}
                    >
                      {pushLoading ? '...' : pushEnabled ? 'Disable' : 'Enable'}
                    </Button>
                  </div>
                  {pushPermission === 'default' && !pushEnabled && (
                    <p className="text-xs text-gray-400 mt-3">
                      When you click Enable, your browser will show a permission popup. Click "Allow" to receive notifications.
                    </p>
                  )}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Support & Feedback */}
        <div className="glass rounded-3xl p-8 mt-8">
          <h3 className="text-2xl font-bold mb-4">Support & Feedback</h3>
          <p className="text-gray-600 mb-4">
            Have questions, feedback, or need help? We're here for you!
          </p>
          <div className="bg-gradient-to-r from-teal/10 to-coral/10 rounded-2xl p-6">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-12 h-12 bg-teal text-white rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p className="font-semibold text-lg">Contact Support</p>
                <p className="text-teal font-medium">
                  cultrameet@gmail.com
                </p>
              </div>
            </div>
            <Button
              onClick={() => window.open('/contact', '_blank')}
              className="w-full mt-4 py-3 rounded-xl bg-white text-black border-2 border-teal hover:bg-teal hover:text-white font-semibold"
            >
              Open Contact Form
            </Button>
            <p className="text-xs text-gray-600 mt-3 text-center">
              Opens in a new tab with your user ID pre-filled
            </p>
          </div>
        </div>

        {/* Safety Tips */}
        <div className="glass rounded-3xl p-8 mt-8">
          <h3 className="text-2xl font-bold mb-4">Safety Tips</h3>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start space-x-2">
              <span className="text-coral font-bold mt-1">•</span>
              <span>Never share personal information like your home address or financial details</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-coral font-bold mt-1">•</span>
              <span>Meet in public places when meeting someone in person for the first time</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-coral font-bold mt-1">•</span>
              <span>Trust your instincts - if something feels wrong, report or block the user</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-coral font-bold mt-1">•</span>
              <span>Report any inappropriate behavior or content immediately</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Settings;